// Adjacency follows the already-rendered catalog, including its group order.
// This owner never repeats query matching, sorting, or catalog loading.
export function readFishDetailSequence(grid) {
  if (!grid) throw new TypeError("Fish detail navigation requires a catalog grid");
  return [...grid.querySelectorAll('[data-fish-id]')].map(card => {
    const itemId = Number(card.dataset.fishId);
    const link = card.querySelector('.fishydex-card-open');
    if (!Number.isSafeInteger(itemId) || itemId <= 0) throw new TypeError('Invalid catalog fish ID');
    return { itemId, href: link?.getAttribute('href') || null };
  });
}

export function fishDetailNeighbours(entries, itemId) {
  const index = entries.findIndex(entry => entry.itemId === itemId);
  return {
    position: index >= 0 ? { current: index + 1, total: entries.length } : null,
    previous: index > 0 ? entries[index - 1] : null,
    next: index >= 0 && index + 1 < entries.length ? entries[index + 1] : null,
  };
}

export function mountFishDetailNavigation({ root, navigate }) {
  const region = root?.querySelector('[data-fish-detail-navigation]');
  if (!region) return null;
  const links = {
    previous: region.querySelector('[data-fish-detail-neighbour="previous"]'),
    next: region.querySelector('[data-fish-detail-neighbour="next"]'),
  };
  const counter = region.querySelector("[data-fish-detail-position]");
  const lifecycle = new AbortController();
  let catalog = { kind: 'loading' };
  let selected = 0;
  let adjacent = fishDetailNeighbours([], 0);

  function render() {
    adjacent = fishDetailNeighbours(catalog.kind === 'ready' ? catalog.entries : [], selected);
    counter.hidden = adjacent.position === null;
    counter.textContent = adjacent.position ? `${adjacent.position.current} / ${adjacent.position.total}` : "";
    for (const direction of ['previous', 'next']) {
      const link = links[direction];
      const entry = adjacent[direction];
      const enabled = Boolean(entry?.href);
      if (enabled) link.setAttribute('href', entry.href);
      else link.removeAttribute('href');
      link.setAttribute('aria-disabled', String(!enabled));
      link.classList.toggle('btn-disabled', !enabled);
      link.tabIndex = enabled ? 0 : -1;
    }
  }

  region.addEventListener('click', event => {
    const link = event.target.closest?.('[data-fish-detail-neighbour]');
    if (!link || !region.contains(link)) return;
    const entry = adjacent[link.dataset.fishDetailNeighbour];
    if (!entry?.href) { event.preventDefault(); return; }
    if (!navigate || event.defaultPrevented || event.button > 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (navigate(entry)) {
      event.preventDefault();
      // Keep focus inside the modal even when the last/first link becomes disabled.
      if (link.getAttribute('aria-disabled') === 'true') {
        root.querySelector('#fishydex-details-title')?.focus({ preventScroll: true });
      }
    }
  }, { signal: lifecycle.signal });
  render();
  return {
    setCatalog(value) {
      if (!['loading', 'unavailable', 'ready'].includes(value.kind)
        || (value.kind === 'ready' && !Array.isArray(value.entries))) {
        throw new TypeError('Invalid fish detail navigation projection');
      }
      catalog = value;
      render();
    },
    select(itemId) { selected = itemId; render(); },
    dispose() { lifecycle.abort(); },
  };
}
